import { ChangeEvent, useCallback, useEffect, useRef } from 'react';

// TODO: временный (надеюсь) общедоступный хук. Нужен до того момента, пока не заменены все кастомные компоненты
// инпута номера телефона на общий PhoneInput. Как полная замена произойдет - везде убрать использование
// этого хука. Оставить только в самом PhoneInput

/**
 * Хук для инпута номера телефона.
 *
 * Обрабатывает вставку из буфера: если вставляемый номер содержит более 10 цифр
 * (т.е. включает код страны +7 или 8), первая цифра автоматически отсекается,
 * после чего значение программно устанавливается в инпут — ReactInputMask
 * форматирует его и вызывает onChange в привычном формате.
 *
 * Возвращает `inputRef` (вешать на инпут) и `onChange` (передавать в ReactInputMask или инпут напрямую).
 */
export const usePhoneInput = (
  callback: ((event: ChangeEvent<HTMLInputElement>) => void) | undefined
) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const handlePaste = useCallback((event: ClipboardEvent) => {
    const digits =
      event.clipboardData?.getData('text').replace(/\D/g, '') ?? '';

    if (digits.length <= 10) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const cleaned = digits.slice(1, 11);

    if (inputRef.current) {
      // React подменяет стандартный сеттер value у инпута, поэтому простое
      // input.value = "..." он проигнорирует. Берём оригинальный браузерный
      // сеттер напрямую с прототипа и вызываем его в обход React.
      // Затем вручную диспатчим событие input — React его слышит и запускает
      // свой цикл обновления, что приводит к вызову onChange.
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;
      nativeSetter?.call(inputRef.current, cleaned);
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, []);

  useEffect(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    input.addEventListener('paste', handlePaste, true);

    return () => input.removeEventListener('paste', handlePaste, true);
  }, [handlePaste]);

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    callbackRef.current?.(event);
  }, []);

  return { inputRef, onChange };
};
